<template>
  <div class="dashboard-admin-sales">
    <MetaManager
        title="Admin - Sales"
        description="Sales from local businesses"
      />
    <h1>Sales</h1>
    <div class="controls-container">
      <div class="date-range-picker">
        <label for="start-date">Start Date:</label>
        <input type="date" v-model="startDate" id="start-date">
        <label for="end-date">End Date:</label>
        <input type="date" v-model="endDate" id="end-date">
        <button @click="fetchFinancialData" class="fetch-button">Fetch Data</button>
      </div>
      <div class="filter-container">
        <label for="min-amount">Minimum Order Amount ($):</label>
        <input 
          type="number" 
          id="min-amount" 
          v-model="minOrderAmount" 
          placeholder="Min Amount" 
          min="0" 
          step="0.01"
          @input="applyFilters"
        >
        <button @click="exportToExcel" class="export-button">
          <i class="fas fa-file-excel"></i> Export to Excel
        </button>
      </div>
    </div>
    <div class="summary-boxes">
      <div class="summary-box">
        <h2>Total Orders</h2>
        <p>{{ filteredOrders.length }}</p>
      </div>
      <div class="summary-box">
        <h2>Total Payouts</h2>
        <p>${{ calculateTotal('payout_to_business').toFixed(2) }}</p>
      </div>
      <div class="summary-box">
        <h2>Our Profit</h2>
        <p>${{ calculateTotal('shop_stark_profit').toFixed(2) }}</p>
      </div>
    </div>
    
    <div class="table-controls">
      <div class="search-container">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search orders..." 
          @input="applyFilters"
          class="search-input"
        >
      </div>
      <div class="results-info">
        Showing {{ filteredOrders.length }} of {{ groupedOrders.length }} orders
      </div>
    </div>
    
    <div class="orders-container">
      <table class="orders-table desktop-table">
        <thead>
          <tr>
            <th @click="sortBy('order_id')">
              Order ID 
              <span class="sort-icon" v-if="sortColumn === 'order_id'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('business_name')">
              Business Name
              <span class="sort-icon" v-if="sortColumn === 'business_name'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('user_name')">
              Customer Name
              <span class="sort-icon" v-if="sortColumn === 'user_name'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('user_email')">
              Customer Email
              <span class="sort-icon" v-if="sortColumn === 'user_email'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('total_amount')">
              Total Amount
              <span class="sort-icon" v-if="sortColumn === 'total_amount'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('stripe_fee')">
              Fees
              <span class="sort-icon" v-if="sortColumn === 'stripe_fee'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('shop_stark_profit')">
              TrioTech Profit
              <span class="sort-icon" v-if="sortColumn === 'shop_stark_profit'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('payout_to_business')">
              Payout to Business
              <span class="sort-icon" v-if="sortColumn === 'payout_to_business'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
            <th @click="sortBy('created_at')">
              Created At
              <span class="sort-icon" v-if="sortColumn === 'created_at'">
                {{ sortDirection === 'asc' ? '▲' : '▼' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in filteredOrders" :key="order.id" @click="toggleOrderDetails(order.order_id)" class="order-row">
            <td>{{ order.order_id }}</td>
            <td>{{ order.business_name }}</td>
            <td>{{ order.user_name }}</td>
            <td>{{ order.user_email }}</td>
            <td>${{ order.total_amount.toFixed(2) }}</td>
            <td>${{ parseFloat(order.stripe_fee).toFixed(2) }}</td>
            <td>${{ parseFloat(order.shop_stark_profit).toFixed(2) }}</td>
            <td>${{ parseFloat(order.payout_to_business).toFixed(2) }}</td>
            <td>{{ formatDate(order.created_at) }}</td>
          </tr>
        </tbody>
      </table>
      
      <div class="mobile-cards">
        <div v-for="order in filteredOrders" :key="order.id" class="order-card" @click="toggleOrderDetails(order.order_id)">
          <div class="order-header">
            <h3>Order #{{ order.order_id }}</h3>
            <span class="order-date">{{ formatDate(order.created_at) }}</span>
          </div>
          <div class="order-details">
            <p><strong>Business:</strong> {{ order.business_name }}</p>
            <p><strong>Customer:</strong> {{ order.user_name }}</p>
            <p><strong>Email:</strong> {{ order.user_email }}</p>
            <p><strong>Total Amount:</strong> ${{ order.total_amount.toFixed(2) }}</p>
            <p><strong>Fees:</strong> ${{ parseFloat(order.stripe_fee).toFixed(2) }}</p>
            <p><strong>TrioTech Profit:</strong> ${{ parseFloat(order.shop_stark_profit).toFixed(2) }}</p>
            <p><strong>Payout to Business:</strong> ${{ parseFloat(order.payout_to_business).toFixed(2) }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MetaManager from '../../components/MetaManager.vue';

export default {
  name: 'DashboardAdminSales',
  components: {
    MetaManager,
  },
  data() {
    return {
      totalOrders: 0,
      totalPayouts: 0,
      totalProfit: 0,
      orders: [],
      groupedOrders: [], // Stores the orders grouped by order_id
      filteredOrders: [], // Stores filtered and sorted orders
      startDate: '',
      endDate: '',
      minOrderAmount: '', // For filtering by minimum order amount
      searchQuery: '', // For searching orders
      sortColumn: 'created_at', // Default sort column
      sortDirection: 'desc', // Default sort direction
      expandedOrderIds: new Set(), // Track which orders are expanded
    };
  },
  methods: {
    setDefaultDateRange() {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      this.startDate = firstDayOfMonth.toISOString().split('T')[0];
      const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      this.endDate = lastDayOfMonth.toISOString().split('T')[0];
    },
    async fetchFinancialData() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_URL}/api/payment/financials`, {
          params: {
            startDate: this.startDate,
            endDate: this.endDate
          }
        });
        this.totalOrders = response.data.totalOrders;
        this.totalPayouts = response.data.totalPayouts;
        this.totalProfit = response.data.totalProfit;
        
        // Process the received orders
        this.orders = response.data.orders.map(order => ({
          ...order,
          total_amount: parseFloat(order.total_before_fee)
        }));
        
        // Group orders by order_id
        this.processOrders();
      } catch (error) {
        console.error('Error fetching financial data:', error);
      }
    },
    processOrders() {
      // Create a map to group orders by order_id
      const orderMap = new Map();
      
      this.orders.forEach(order => {
        if (!orderMap.has(order.order_id)) {
          orderMap.set(order.order_id, order);
        } else {
          // If order already exists, merge or update as needed
          const existingOrder = orderMap.get(order.order_id);
          existingOrder.total_amount += parseFloat(order.total_amount);
          existingOrder.payout_to_business = (parseFloat(existingOrder.payout_to_business) + parseFloat(order.payout_to_business)).toString();
          existingOrder.shop_stark_profit = (parseFloat(existingOrder.shop_stark_profit) + parseFloat(order.shop_stark_profit)).toString();
          existingOrder.stripe_fee = (parseFloat(existingOrder.stripe_fee) + parseFloat(order.stripe_fee)).toString();
        }
      });
      
      // Convert map to array
      this.groupedOrders = Array.from(orderMap.values());
      this.applyFilters();
    },
    applyFilters() {
      let filtered = [...this.groupedOrders];
      
      // Apply minimum amount filter if provided
      if (this.minOrderAmount && !isNaN(parseFloat(this.minOrderAmount))) {
        const minAmount = parseFloat(this.minOrderAmount);
        filtered = filtered.filter(order => {
          // Ensure we're using numeric comparison by converting to float
          const orderAmount = parseFloat(order.total_amount);
          return orderAmount >= minAmount;
        });
      }
      
      // Apply search query if provided
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(order => 
          String(order.order_id).includes(query) ||
          order.business_name.toLowerCase().includes(query) ||
          order.user_name.toLowerCase().includes(query) ||
          order.user_email.toLowerCase().includes(query)
        );
      }
      
      // Apply sorting
      filtered.sort((a, b) => {
        let valueA, valueB;
        
        // Handle numeric and date fields
        if (['total_amount', 'stripe_fee', 'shop_stark_profit', 'payout_to_business'].includes(this.sortColumn)) {
          valueA = parseFloat(a[this.sortColumn]);
          valueB = parseFloat(b[this.sortColumn]);
        } else if (this.sortColumn === 'created_at') {
          valueA = new Date(a[this.sortColumn]);
          valueB = new Date(b[this.sortColumn]);
        } else {
          valueA = a[this.sortColumn];
          valueB = b[this.sortColumn];
        }
        
        // Sort based on direction
        if (this.sortDirection === 'asc') {
          return valueA > valueB ? 1 : -1;
        } else {
          return valueA < valueB ? 1 : -1;
        }
      });
      
      this.filteredOrders = filtered;
    },
    sortBy(column) {
      // If clicking the same column, toggle direction
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc'; // Default to ascending for new column
      }
      
      this.applyFilters();
    },
    toggleOrderDetails(orderId) {
      if (this.expandedOrderIds.has(orderId)) {
        this.expandedOrderIds.delete(orderId);
      } else {
        this.expandedOrderIds.add(orderId);
      }
    },
    formatDate(dateString) {
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    calculateTotal(field) {
      return this.filteredOrders.reduce((sum, order) => {
        return sum + parseFloat(order[field]);
      }, 0);
    },
    exportToExcel() {
      // Define the headers for the Excel file with new columns
      const headers = [
        'Order ID', 
        'Business Name',
        'Customer Name',
        'Customer Email',
        'Total Amount', 
        'Fees', 
        'TrioTech Profit', 
        'Payout to Business', 
        'Created At'
      ];
      
      // Helper function to properly escape CSV fields
      const escapeCSV = (field) => {
        if (field === null || field === undefined) {
          return '""';
        }
        
        // Convert to string and wrap in quotes, escaping any existing quotes
        const stringField = String(field);
        return `"${stringField.replace(/"/g, '""')}"`;
      };
      
      // Map order data and escape each field
      const rows = this.filteredOrders.map(order => [
        escapeCSV(order.order_id),
        escapeCSV(order.business_name),
        escapeCSV(order.user_name),
        escapeCSV(order.user_email),
        escapeCSV('$' + order.total_amount.toFixed(2)),
        escapeCSV('$' + parseFloat(order.stripe_fee).toFixed(2)),
        escapeCSV('$' + parseFloat(order.shop_stark_profit).toFixed(2)),
        escapeCSV('$' + parseFloat(order.payout_to_business).toFixed(2)),
        escapeCSV(this.formatDate(order.created_at))
      ]);
      
      // Create CSV header with escaped fields
      const headerRow = headers.map(header => escapeCSV(header)).join(',');
      
      // Join rows (each field is already escaped and separated by commas)
      const dataRows = rows.map(row => row.join(',')).join('\n');
      
      // Combine header and data
      const csvContent = headerRow + '\n' + dataRows;

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Sales_${this.startDate}_to_${this.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  },
  watch: {
    // Add watchers to ensure filters are applied when values change
    minOrderAmount() {
      this.applyFilters();
    },
    searchQuery() {
      this.applyFilters();
    }
  },
  mounted(){
    this.setDefaultDateRange();
    this.fetchFinancialData();
  }
};
</script>

<style scoped>
.dashboard-admin-sales {
  padding: 20px;
  font-family: 'Arial', sans-serif;
  margin-left: 300px;
  background-color: #f9f9f9;
}

h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
  font-weight: 700;
}

.controls-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 25px;
  background-color: #fff;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.date-range-picker, .filter-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.date-range-picker label, .filter-container label {
  font-weight: 600;
  color: #4C6B30;
}

.date-range-picker input, .filter-container input {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  transition: border-color 0.2s;
}

.date-range-picker input:focus, .filter-container input:focus {
  border-color: #4C6B30;
  outline: none;
}

.fetch-button, .export-button {
  padding: 8px 15px;
  background-color: #4C6B30;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.fetch-button:hover, .export-button:hover {
  background-color: #6E9F49;
}

.summary-boxes {
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 15px;
}

.summary-box {
  flex: 1;
  min-width: 200px;
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

.summary-box:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.summary-box h2 {
  font-size: 1.1em;
  margin-bottom: 10px;
  color: #4C6B30;
}

.summary-box p {
  font-size: 1.8em;
  font-weight: 700;
  color: #333;
}

.table-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.search-container {
  flex: 1;
  max-width: 300px;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
}

.results-info {
  font-size: 0.9em;
  color: #666;
}

.orders-container {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.desktop-table {
  width: 100%;
  border-collapse: collapse;
}

.desktop-table th,
.desktop-table td {
  padding: 12px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.desktop-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #4C6B30;
  cursor: pointer;
  user-select: none;
  position: relative;
}

.desktop-table th:hover {
  background-color: #e9e9e9;
}

.sort-icon {
  margin-left: 5px;
  color: #4C6B30;
}

.desktop-table tr:hover {
  background-color: #f9f9f9;
}

.order-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.order-row.expanded {
  background-color: #f5f5f5;
}

.mobile-cards {
  display: none;
}

.order-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.order-header h3 {
  margin: 0;
  color: #4C6B30;
}

.order-date {
  font-size: 0.9em;
  color: #666;
}

.order-details p {
  margin: 5px 0;
  display: flex;
  justify-content: space-between;
}

@media(max-width: 1024px){
  .dashboard-admin-sales{
    margin-left: 0;
  }
  
  .controls-container {
    flex-direction: column;
    gap: 15px;
  }
  
  .date-range-picker, .filter-container {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .dashboard-admin-sales {
    margin-left: 0;
    padding: 10px;
  }

  .summary-box {
    min-width: 100%;
  }

  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }
  
  .table-controls {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .search-container {
    max-width: 100%;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .controls-container, .summary-boxes {
    padding: 10px;
  }
  
  .order-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}

@media (max-width: 1200px) {
  .desktop-table th, 
  .desktop-table td {
    padding: 8px 6px;
    font-size: 0.9em;
  }
}
</style>